import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import {
  ArrowLeft, Storefront, Plus, MapPin, Trash, CurrencyDollar,
  TrendUp, ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * FarmersMarkets — /farmers/markets
 *
 * Farmer-submitted local prices. Country → State → District filter with
 * a median-price table + weekly trend chart for the picked crop.
 */
export default function FarmersMarkets() {
  const { user, requireSignIn } = useAuth();
  const [prices, setPrices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [regions, setRegions] = useState({ level: "country", regions: [] });
  const [cropList, setCropList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [crop, setCrop] = useState("");
  const [days, setDays] = useState(90);
  const [showForm, setShowForm] = useState(false);

  const filters = useMemo(() => ({ country, state, district, crop, days }), [country, state, district, crop, days]);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (country) p.set("country", country);
    if (state) p.set("state", state);
    if (district) p.set("district", district);
    if (crop) p.set("crop", crop);
    p.set("days", String(days));
    Promise.all([
      http.get(`/farmers/prices?${p.toString()}&limit=60`),
      http.get(`/farmers/prices/summary?${p.toString()}`),
      http.get(`/farmers/prices/regions?${new URLSearchParams({
        ...(country ? { country } : {}),
        ...(state ? { state } : {}),
        days: String(days),
      }).toString()}`),
      http.get(`/farmers/prices/crops?${p.toString()}`),
    ]).then(([a, b, c, d]) => {
      setPrices(a.data.prices || []);
      setSummary(b.data);
      setRegions(c.data);
      setCropList(d.data.crops || []);
    }).catch(() => toast.error("Could not load prices")).finally(() => setLoading(false));
  };
  useEffect(load, [country, state, district, crop, days]); // eslint-disable-line

  const clearFrom = (level) => {
    if (level === "country") { setCountry(""); setState(""); setDistrict(""); }
    else if (level === "state") { setState(""); setDistrict(""); }
    else if (level === "district") setDistrict("");
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this price?")) return;
    try { await http.delete(`/farmers/prices/${pid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const heroText = "Farmer-submitted local prices from mandis, haats, wholesale markets and cooperatives — with a median price and a 3-month trend for every crop and region.";
  const maxTrend = summary?.trend?.length ? Math.max(1, ...summary.trend.map((t) => t.median)) : 1;
  const currentRegion = [country, state, district].filter(Boolean).join(" → ") || "World";

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-markets-page">
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <Storefront size={12} weight="fill" /> / Markets & Prices
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">Local prices, farmer-verified.</h1>
          <p className="mt-2 font-serif-display italic text-[#3A3830] max-w-3xl">
            Every farmer submission strengthens the median. See the current price for your crop in your region — plus a 3-month trend.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar text={heroText} testidPrefix="farmers-markets-tools" />
            <ShareButton title="Markets & Prices — Farmers Pulse" text={heroText} variant="chip" testid="farmers-markets-share" />
            <button
              onClick={() => (user ? setShowForm(true) : requireSignIn("submit a price"))}
              data-testid="farmers-markets-submit-cta"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              <Plus size={11} weight="bold" /> Submit a price
            </button>
          </div>
        </header>

        {/* Region breadcrumb */}
        <nav className="mt-6 flex flex-wrap items-center gap-2" data-testid="farmers-markets-crumbs">
          <button onClick={() => clearFrom("country")} data-testid="markets-region-world"
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${!country ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>World</button>
          {country && <>→ <button onClick={() => clearFrom("state")} className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${!state ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15"}`}>{country}</button></>}
          {state && <>→ <button onClick={() => clearFrom("district")} className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${!district ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15"}`}>{state}</button></>}
          {district && <>→ <span className="rounded-full border border-black bg-[#1B4332] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">{district}</span></>}
          <button onClick={load} data-testid="markets-refresh" className="ml-auto inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:border-black/40">
            <ArrowsClockwise size={11} weight="bold" /> Refresh
          </button>
        </nav>

        {/* Next-level region drill-down */}
        {(regions.regions?.length > 0 && (!country || !state)) && (
          <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4" data-testid="markets-regions">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {regions.level === "country" ? "Countries with prices" : regions.level === "state" ? "States with prices" : "Districts with prices"}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {regions.regions.slice(0, 30).map((r) => (
                <button
                  key={r.name}
                  onClick={() => {
                    if (regions.level === "country") setCountry(r.name);
                    else if (regions.level === "state") setState(r.name);
                    else setDistrict(r.name);
                  }}
                  data-testid={`markets-region-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-full border border-black/15 bg-[#FBF2E1]/50 px-3 py-1.5 text-sm hover:border-[color:var(--np-brand,#B85F2A)]"
                >
                  {r.name} <span className="ml-1 font-mono-tech text-[10px] text-[#3A3830]">{r.count}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Crop filter chips */}
        <div className="mt-4 flex flex-wrap gap-2 items-center" data-testid="markets-crop-filters">
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Crop:</span>
          <button onClick={() => setCrop("")} data-testid="markets-crop-all"
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${crop === "" ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>All</button>
          {cropList.slice(0, 20).map((c) => (
            <button key={c.crop} onClick={() => setCrop(crop === c.crop ? "" : c.crop)}
                    data-testid={`markets-crop-${c.crop}`}
                    className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase capitalize ${crop === c.crop ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>
              {c.crop} · {c.count}
            </button>
          ))}
          <span className="w-2" />
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Days:</span>
          {[30, 90, 180, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`markets-days-${d}`}
                    className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${days === d ? "bg-[#1B4332] text-[#F5EBDC] border-black" : "bg-white border-black/15 hover:border-black/40"}`}>{d}d</button>
          ))}
        </div>

        {/* Submit form */}
        {showForm && <SubmitPriceForm defaults={filters} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

        {/* Median summary */}
        <section className="mt-6" data-testid="markets-summary">
          <div className="flex items-center gap-3">
            <h2 className="font-serif-display text-2xl">
              Median prices in {currentRegion}
            </h2>
            <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">last {days} days</span>
          </div>
          {loading ? (
            <div className="mt-3 h-24 rounded-2xl bg-white/60 animate-pulse" />
          ) : (summary?.by_crop || []).length === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-black/20 bg-white/70 p-8 text-center">
              <CurrencyDollar size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
              <div className="font-serif-display text-2xl">No price submissions here yet.</div>
              <p className="mt-1 text-sm text-[#3A3830]">Be the first — every farmer submission builds the picture.</p>
            </div>
          ) : (
            <div className="mt-3 rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="markets-medians">
              <table className="w-full text-sm">
                <thead className="bg-[#FBF2E1] text-left">
                  <tr>
                    <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Crop</th>
                    <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Median</th>
                    <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Range</th>
                    <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Submissions</th>
                    <th className="p-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase">Latest</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_crop.map((r, i) => (
                    <tr key={i} className="border-t border-black/5 cursor-pointer hover:bg-[#FBF2E1]/40"
                        onClick={() => setCrop(r.crop)}
                        data-testid={`markets-median-row-${r.crop}`}>
                      <td className="p-2 capitalize font-serif-display">{r.crop}</td>
                      <td className="p-2 font-mono-tech text-base">{r.median} <span className="text-[10px] text-[#3A3830]">{r.currency}/{r.unit}</span></td>
                      <td className="p-2 text-[#3A3830]">{r.min} – {r.max}</td>
                      <td className="p-2">{r.count}</td>
                      <td className="p-2">{r.latest}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Trend chart for picked crop */}
        {crop && summary?.trend?.length > 0 && (
          <section className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="markets-trend">
            <div className="flex items-center gap-2">
              <TrendUp size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
              <h2 className="font-serif-display text-2xl capitalize">{crop} — weekly median trend</h2>
            </div>
            <div className="mt-4 grid gap-1 items-end h-40" style={{ gridTemplateColumns: `repeat(${summary.trend.length}, minmax(0, 1fr))` }}>
              {summary.trend.map((t) => (
                <div key={t.week} className="h-full flex flex-col items-center justify-end gap-1">
                  <div
                    className="w-full rounded-t bg-[color:var(--np-brand,#B85F2A)]/80"
                    style={{ height: `${Math.max(4, (t.median / maxTrend) * 100)}%` }}
                    title={`${t.week}: ${t.median}`}
                  />
                  <div className="font-mono-tech text-[8px] tracking-[0.2em] uppercase text-[#3A3830]">
                    {t.week.slice(5)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Recent submissions */}
        <section className="mt-6" data-testid="markets-recent">
          <div className="flex items-center gap-2">
            <h2 className="font-serif-display text-2xl">Recent submissions</h2>
            <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{prices.length} entries</span>
          </div>
          {prices.length === 0 ? (
            <div className="mt-3 text-sm text-[#3A3830]">No submissions in this filter.</div>
          ) : (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {prices.map((p) => (
                <div key={p.id} className="rounded-xl border border-black/10 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] capitalize">
                        {p.crop}{p.variety ? ` · ${p.variety}` : ""}{p.market_type ? ` · ${p.market_type}` : ""}
                      </div>
                      <div className="mt-1 font-serif-display text-xl">
                        {p.price} <span className="text-sm text-[#3A3830]">{p.currency}/{p.unit}</span>
                      </div>
                      {p.market_name && <div className="mt-0.5 text-xs text-[#3A3830]">{p.market_name}</div>}
                      <div className="mt-1 text-[11px] text-[#3A3830] inline-flex items-center gap-1">
                        <MapPin size={10} weight="fill" />
                        {[p.location?.village, p.location?.district, p.location?.state, p.location?.country].filter(Boolean).join(", ")}
                      </div>
                      <div className="mt-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
                        {p.owner_name} · {p.entry_date}
                      </div>
                      {p.notes && <p className="mt-1 text-xs text-[#3A3830] whitespace-pre-wrap">{p.notes}</p>}
                    </div>
                    {user && user.firebase_uid === p.owner_uid && (
                      <button onClick={() => remove(p.id)} data-testid={`market-price-delete-${p.id}`}
                              className="p-1.5 rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700">
                        <Trash size={11} weight="fill" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}


// =====================================================================
//  SUBMIT PRICE FORM
// =====================================================================
function SubmitPriceForm({ defaults, onClose, onCreated }) {
  const [crop, setCrop] = useState(defaults.crop || "");
  const [variety, setVariety] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("kg");
  const [currency, setCurrency] = useState("INR");
  const [marketName, setMarketName] = useState("");
  const [marketType, setMarketType] = useState("mandi");
  const [quality, setQuality] = useState("");
  const [country, setCountry] = useState(defaults.country || "");
  const [state, setState] = useState(defaults.state || "");
  const [district, setDistrict] = useState(defaults.district || "");
  const [village, setVillage] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!crop.trim()) return toast.error("Enter a crop");
    if (!price) return toast.error("Enter a price");
    if (!country.trim()) return toast.error("Enter a country");
    setBusy(true);
    try {
      await http.post("/farmers/prices", {
        crop: crop.trim().toLowerCase(),
        variety: variety.trim() || undefined,
        price: parseFloat(price),
        unit,
        currency: currency.trim().toUpperCase(),
        market_name: marketName.trim() || undefined,
        market_type: marketType || undefined,
        source: "farmer",
        quality: quality.trim() || undefined,
        location: {
          country: country.trim(),
          state: state.trim() || undefined,
          district: district.trim() || undefined,
          village: village.trim() || undefined,
        },
        notes: notes.trim() || undefined,
      });
      toast.success("Price submitted — thank you");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-black/15 bg-white p-5" data-testid="markets-submit-form">
      <div className="flex items-center gap-2">
        <CurrencyDollar size={16} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
        <div className="font-serif-display text-xl">Submit a local price</div>
        <button onClick={onClose} className="ml-auto font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830] hover:text-[#1B4332]">Cancel</button>
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Fld label="Crop *" value={crop} setValue={setCrop} testid="price-crop" placeholder="tomato" />
        <Fld label="Variety" value={variety} setValue={setVariety} testid="price-variety" />
        <Fld label="Price *" value={price} setValue={setPrice} testid="price-value" placeholder="45" />
        <Sel label="Unit" value={unit} setValue={setUnit} testid="price-unit"
             options={["kg","g","quintal","ton","lb","bushel","piece","dozen","bag","crate","sack","liter","gallon","50kg-bag","40kg-bag"]} />
        <Fld label="Currency" value={currency} setValue={setCurrency} testid="price-currency" placeholder="INR" />
        <Sel label="Market type" value={marketType} setValue={setMarketType} testid="price-market-type"
             options={["mandi","haat","wholesale","retail","cooperative","farm-gate","export"]} />
        <Fld label="Market name" value={marketName} setValue={setMarketName} testid="price-market-name" placeholder="Ernakulam mandi" />
        <Fld label="Quality" value={quality} setValue={setQuality} testid="price-quality" placeholder="A / premium" />
        <div />
        <Fld label="Country *" value={country} setValue={setCountry} testid="price-country" placeholder="India" />
        <Fld label="State" value={state} setValue={setState} testid="price-state" placeholder="Kerala" />
        <Fld label="District" value={district} setValue={setDistrict} testid="price-district" placeholder="Ernakulam" />
        <Fld label="Village" value={village} setValue={setVillage} testid="price-village" />
      </div>
      <label className="mt-3 block">
        <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Notes</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="price-notes" lang="auto" dir="auto"
                  rows={2} className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
      </label>
      <div className="mt-4 flex justify-end">
        <button onClick={submit} disabled={busy} data-testid="market-submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
          {busy ? "Submitting…" : "Submit price"}
        </button>
      </div>
    </div>
  );
}

const Fld = ({ label, value, setValue, testid, placeholder }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <input value={value} onChange={(e) => setValue(e.target.value)} data-testid={testid} lang="auto" dir="auto"
           placeholder={placeholder}
           className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
  </label>
);
const Sel = ({ label, value, setValue, options, testid }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <select value={value} onChange={(e) => setValue(e.target.value)} data-testid={testid}
            className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
